// AuthPanel.tsx
import { AuthModalTab } from "@/context/Auth";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { User, UserCredential } from "firebase/auth";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import LoginForm from "./LoginForm";
import OTPform from "./OTPForm";
import ResetPassForm from "./ResetPassForm";
import SignupForm from "./SignupForm";
import { useAuthContext } from "@/context/Auth";
import PrivacyPolicyPopup from "../profile/PrivacyPolicyPopup";

const Root = styled("div", {
  shouldForwardProp: (prop: PropertyKey) => ![""].includes(prop.toString()),
})<Partial<AuthPanelCombinedProps>>((props) => css``);

export interface AuthPanelProps {
  defaultTab?: AuthModalTab;
  onCloseRequest?: () => void;
}

export type AuthPanelCombinedProps = AuthPanelProps &
  JSX.IntrinsicElements["div"];

const AuthPanel: React.FC<AuthPanelCombinedProps> = ({
  defaultTab = "login",
  onCloseRequest,
  ...props
}) => {
  const router = useRouter();
  const [activeTab, setActiveTab] = React.useState<AuthModalTab>(defaultTab);
  const [user, setUser] = useState<User | null>(null);
  const [trustDevice, setTrustDevice] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAuthProcessing, setIsAuthProcessing] = useState<boolean>(false);
  const { dispatch } = useAuthContext();
  const [privacyPolicyOpened, setPrivacyPolicyOpened] = useState(false);

  const isDeviceTrusted = (userId: string): boolean => {
    try {
      const trustedDeviceKey = `trusted_device_${userId}`;
      const firstLoginKey = `first_login_complete_${userId}`;
      return (
        localStorage.getItem(trustedDeviceKey) === 'true' &&
        localStorage.getItem(firstLoginKey) === 'true'
      );
    } catch (error) {
      console.error('Error checking device trust status:', error);
      return false;
    }
  };

  const isPrivacyPolicyAccepted = () => {
    return localStorage.getItem('privacy_policy_accepted') === 'true';
  };

  const handleSuccessfulAuth = React.useCallback(async () => {
    try {
      onCloseRequest?.();
      // First try Next.js router
      await router.push('/profile');
      // If Next.js router fails, use window.location after a short delay
      setTimeout(() => {
        if (window.location.pathname !== '/profile') {
          window.location.href = "/profile";
        }
      }, 100);
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback navigation
      window.location.href = "/profile";
    }
  }, [onCloseRequest, router]);

  const handleAuth = React.useCallback(async (creds: UserCredential) => {
    if (isAuthProcessing) {
      return;
    }

    try {
      setIsAuthProcessing(true);
      setError(null);
      
      // Get user token
      const token = await creds.user.getIdToken();
      
      const isGoogleUser = creds.user.providerData.some(
        provider => provider.providerId === 'google.com'
      );

      // First verify session to get user role
      const verifyResponse = await fetch('/api/auth/verify-session', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify session');
      }

      const verifyData = await verifyResponse.json();
      const userRole = verifyData.userRole;

      // Store privacy policy acceptance in database if it was accepted
      if (isPrivacyPolicyAccepted()) {
        try {
          await fetch('/api/user/privacy-policy', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              accepted: true,
              acceptedAt: new Date().toISOString()
            })
          });
        } catch (error) {
          console.error('Failed to store privacy policy acceptance:', error);
          // Don't throw here, as we still want to proceed with auth
        }
      }

      // Skip OTP for Google users or trusted devices
      if (isGoogleUser || isDeviceTrusted(creds.user.uid)) {
        if (trustDevice) {
          localStorage.setItem(`trusted_device_${creds.user.uid}`, 'true');
          localStorage.setItem(`first_login_complete_${creds.user.uid}`, 'true');
        }
        handleSuccessfulAuth();
        return;
      }

      // Create OTP for non-Google users
      const response = await fetch("/api/auth/create-otp", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ trustDevice })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to setup verification');
      }

      setUser(creds.user);
      setActiveTab("verify-otp");
      
    } catch (error) {
      console.error('Error during authentication:', error);
      setError(error instanceof Error ? error.message : 'Authentication failed');
    } finally {
      setIsAuthProcessing(false);
    }
  }, [isAuthProcessing, trustDevice, handleSuccessfulAuth, isPrivacyPolicyAccepted]);

  const handleSignupRequest = () => {
    setActiveTab("signup");
  };

  const handlePrivacyPolicyAccept = () => {
    dispatch({ type: 'PRIVACY_POLICY_ACCEPTED' });
    setPrivacyPolicyOpened(false);
    setActiveTab("signup");
  };

  return (
    <Root {...props} style={{ overflow: "auto", height: "100%" }}>
      {error && (
        <div className="text-red-500 text-sm mb-4 p-2 bg-red-50 rounded">
          {error}
        </div>
      )}
      {
        {
          login: (
            <LoginForm
              onLogin={handleAuth}
              onSignupRequest={handleSignupRequest}
              onPasswordResetRequest={() => setActiveTab("resetpass")}
              trustDevice={trustDevice}
              onTrustDeviceChange={setTrustDevice}
            />
          ),
          signup: (
            <SignupForm
              onLoginRequest={() => setActiveTab("login")}
              onCloseRequest={onCloseRequest}
            />
          ),
          resetpass: (
            <ResetPassForm
              onLoginRequest={() => setActiveTab("login")}
              onCloseRequest={onCloseRequest}
            />
          ),
          "verify-otp": (
            <OTPform
              user={user}
              trustDevice={trustDevice}
              onClose={async (verified) => {
                if (verified && user) {
                  try {
                    const token = await user.getIdToken();
                    const verifyResponse = await fetch('/api/auth/verify-session', {
                      method: 'POST',
                      headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    if (!verifyResponse.ok) {
                      throw new Error('Failed to verify session');
                    }
                    const verifyData = await verifyResponse.json();
                    handleSuccessfulAuth();
                  } catch (error) {
                    console.error('Error during OTP verification:', error);
                    setError(error instanceof Error ? error.message : 'Verification failed');
                  }
                }
              }}
            />
          ),
        }[activeTab]
      }

      <PrivacyPolicyPopup 
        opened={privacyPolicyOpened} 
        onClose={() => setPrivacyPolicyOpened(false)}
        onAccept={handlePrivacyPolicyAccept}
      />
    </Root>
  );
};

export default AuthPanel;